import { ChangeDetectionStrategy, Component, type OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CloudApiService, type CapabilityDescriptor, type RoleDefinition } from '@/cloud/services/cloud-api.service';
import { BreadcrumbsComponent, type BreadcrumbItem } from '@/shared/breadcrumbs/breadcrumbs.component';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';

/**
 * Role definitions editor. System roles (Reader/Writer/Admin) are read-only;
 * custom roles pick capabilities from the catalog, grouped by area.
 */
@Component({
    selector: 'app-roles',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, CheckboxModule, DialogModule, InputTextModule, TableModule, TagModule, TooltipModule, ConfirmDialogModule, BreadcrumbsComponent, EmptyStateComponent],
    providers: [ConfirmationService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: './roles.scss',
    template: `
        <p-confirmdialog />
        <div class="management-page">
            <app-breadcrumbs [model]="breadcrumbs" />
            <header class="overview-header">
                <div>
                    <p class="overview-eyebrow">Administration</p>
                    <h1>Roles</h1>
                    <p class="overview-description">Roles define what members can do in a tenant. System roles are fixed; custom roles pick from the capability catalog.</p>
                </div>
                <div class="overview-actions">
                    <p-button label="Create Role" icon="pi pi-plus" (onClick)="openEditor(null)" />
                </div>
            </header>

            <section class="management-panel" aria-label="Roles">
                <p-table class="management-table" [value]="roles()" [loading]="loading()" [scrollable]="true">
                    <ng-template #header>
                        <tr>
                            <th>Name</th>
                            <th>Description</th>
                            <th>Capabilities</th>
                            <th><span class="sr-only">Actions</span></th>
                        </tr>
                    </ng-template>
                    <ng-template #body let-role>
                        <tr>
                            <td>
                                <div class="flex items-center flex-wrap gap-2 min-w-40">
                                    <span class="font-medium">{{ role.name }}</span>
                                    @if (role.isSystem) {
                                        <p-tag value="System" severity="secondary" [rounded]="true" pTooltip="Built-in role — cannot be edited" />
                                    }
                                </div>
                            </td>
                            <td class="text-color-secondary">
                                <div class="min-w-64">{{ role.description }}</div>
                            </td>
                            <td class="whitespace-nowrap">{{ role.capabilities.length }} / {{ catalog().length }}</td>
                            <td>
                                <div class="management-row-actions">
                                    @if (role.isSystem) {
                                        <p-button label="View" icon="pi pi-eye" [text]="true" [ariaLabel]="'View ' + role.name" (onClick)="openEditor(role)" />
                                    } @else {
                                        <p-button icon="pi pi-pencil" [text]="true" [rounded]="true" [ariaLabel]="'Edit ' + role.name" pTooltip="Edit" (onClick)="openEditor(role)" />
                                        <p-button icon="pi pi-trash" [text]="true" [rounded]="true" severity="danger" [ariaLabel]="'Delete ' + role.name" pTooltip="Delete" (onClick)="deleteRole(role)" />
                                    }
                                </div>
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template #emptymessage>
                        <tr>
                            <td colspan="4">
                                <app-empty-state icon="fa fa-user-shield" title="No roles found" />
                            </td>
                        </tr>
                    </ng-template>
                </p-table>
            </section>
        </div>

        <p-dialog [header]="editorHeader()" [visible]="showEditor()" [modal]="true" [closable]="true" (visibleChange)="showEditor.set($event)" [style]="{ width: '44rem', maxHeight: '85vh' }" [contentStyle]="{ overflow: 'auto' }">
            <label class="block mb-1 font-medium" for="role-name">Name</label>
            <input pInputText id="role-name" [(ngModel)]="editName" class="w-full mb-3" [disabled]="readonly() || saving()" />

            <label class="block mb-1 font-medium" for="role-desc">Description</label>
            <input pInputText id="role-desc" [(ngModel)]="editDescription" class="w-full mb-4" [disabled]="readonly() || saving()" />

            <label class="block mb-2 font-medium">Capabilities</label>
            @for (group of groupedCatalog(); track group.name) {
                <div class="mb-3">
                    <div class="text-sm font-semibold text-color-secondary uppercase mb-2">{{ group.name }}</div>
                    @for (cap of group.capabilities; track cap.id) {
                        <div class="flex items-start gap-2 mb-2 ml-1">
                            <p-checkbox [binary]="true" [inputId]="cap.id" [ngModel]="selectedCaps().has(cap.id)" (ngModelChange)="toggleCap(cap.id, $event)" [disabled]="readonly() || saving()" />
                            <label [for]="cap.id" class="cursor-pointer">
                                <span class="font-medium">{{ cap.label }}</span>
                                <span class="block text-sm text-color-secondary">{{ cap.description }}</span>
                            </label>
                        </div>
                    }
                </div>
            }

            <div class="flex justify-end gap-2 mt-4">
                <button pButton type="button" class="p-button-text" (click)="showEditor.set(false)" [disabled]="saving()"><span class="p-button-label">{{ readonly() ? 'Close' : 'Cancel' }}</span></button>
                @if (!readonly()) {
                    <button pButton type="button" (click)="save()" [loading]="saving()" [disabled]="saving() || !editName().trim() || selectedCaps().size === 0">@if (saving()) {<i class="pi pi-spinner pi-spin p-button-loading-icon" aria-hidden="true"></i>}<span class="p-button-label">Save</span></button>
                }
            </div>
        </p-dialog>
    `
})
export class Roles implements OnInit {
    private api = inject(CloudApiService);
    private confirmationService = inject(ConfirmationService);
    private messageService = inject(MessageService);

    readonly breadcrumbs: BreadcrumbItem[] = [
        { label: 'Dashboard', routerLink: '/dashboard' },
        { label: 'Roles', routerLink: '/admin/roles' }
    ];

    loading = signal(false);
    roles = signal<RoleDefinition[]>([]);
    catalog = signal<CapabilityDescriptor[]>([]);

    showEditor = signal(false);
    saving = signal(false);
    readonly = signal(false);
    private editingRole = signal<RoleDefinition | null>(null);
    editName = signal('');
    editDescription = signal('');
    selectedCaps = signal<Set<string>>(new Set());

    editorHeader = computed(() => {
        const role = this.editingRole();
        if (!role) return 'Create Role';
        return role.isSystem ? `${role.name} (System Role)` : `Edit ${role.name}`;
    });

    groupedCatalog = computed(() => {
        const groups = new Map<string, CapabilityDescriptor[]>();
        for (const cap of this.catalog()) {
            const list = groups.get(cap.group) ?? [];
            list.push(cap);
            groups.set(cap.group, list);
        }
        return Array.from(groups.entries()).map(([name, capabilities]) => ({ name, capabilities }));
    });

    ngOnInit(): void {
        this.refresh();
        this.api
            .getCapabilityCatalog()
            .then((catalog) => this.catalog.set(catalog))
            .catch((err) => this.toastError('Failed to load capability catalog', err));
    }

    refresh() {
        this.loading.set(true);
        this.api
            .getRoles(true)
            .then((roles) => this.roles.set(roles))
            .catch((err) => this.toastError('Failed to load roles', err))
            .finally(() => this.loading.set(false));
    }

    openEditor(role: RoleDefinition | null) {
        this.editingRole.set(role);
        this.readonly.set(!!role?.isSystem);
        this.editName.set(role?.name ?? '');
        this.editDescription.set(role?.description ?? '');
        this.selectedCaps.set(new Set(role?.capabilities ?? []));
        this.showEditor.set(true);
    }

    toggleCap(capId: string, checked: boolean) {
        const next = new Set(this.selectedCaps());
        if (checked) next.add(capId);
        else next.delete(capId);
        this.selectedCaps.set(next);
    }

    save() {
        const name = this.editName().trim();
        const description = this.editDescription().trim() || null;
        const capabilities = Array.from(this.selectedCaps());
        const existing = this.editingRole();
        this.saving.set(true);
        const op = existing ? this.api.updateRole(existing.id, name, description, capabilities) : this.api.createRole(name, description, capabilities);
        op.then((role) => {
            this.messageService.add({ severity: 'success', summary: existing ? 'Role Updated' : 'Role Created', detail: role.name });
            this.showEditor.set(false);
            this.refresh();
        })
            .catch((err) => this.toastError('Save Failed', err))
            .finally(() => this.saving.set(false));
    }

    deleteRole(role: RoleDefinition) {
        this.confirmationService.confirm({
            header: 'Delete Role',
            message: `Delete the "${role.name}" role? Users must not have it assigned.`,
            icon: 'pi pi-exclamation-triangle',
            acceptButtonStyleClass: 'p-button-danger',
            acceptLabel: 'Delete',
            accept: () => {
                this.api
                    .deleteRole(role.id)
                    .then(() => {
                        this.messageService.add({ severity: 'success', summary: 'Role Deleted', detail: role.name });
                        this.refresh();
                    })
                    .catch((err) => {
                        if (err?.status === 409) {
                            const count = err?.error?.userCount;
                            this.messageService.add({ severity: 'warn', summary: 'Role In Use', detail: `"${role.name}" is assigned to ${count ?? 'some'} user(s). Reassign them first.` });
                        } else {
                            this.toastError('Delete Failed', err);
                        }
                    });
            }
        });
    }

    private toastError(summary: string, err: any) {
        const detail = err?.error?.message ?? (typeof err?.error === 'string' ? err.error : null) ?? err?.message ?? 'An error occurred';
        this.messageService.add({ severity: 'error', summary, detail });
    }
}
